import React, { FormEvent } from 'react'
import { graphql } from 'react-apollo'
import { Redirect, withRouter } from 'react-router-dom'

import {
  Button,
  ConfirmationModal,
  ModalActionTypes,
  PrivateInput,
  Spinner,
  TextInput,
} from '@acorns/web-components'
import { GenericAnalyticsClient, withAnalyticsClient } from '@acorns/web-utils'
import { useFeature } from '@optimizely/react-sdk'
import { withFormik } from 'formik'
import {
  compose,
  lifecycle,
  withHandlers,
  withProps,
  withState,
} from 'recompose'
import styled from 'styled-components'

import AlertBadge from 'assets/icons/alert-badge.png'
import Layout from 'components/Layout'
import theme from 'theme'
import { withEnv } from 'utils/environment'
import {
  emailSuspensionNotification,
  maintenanceScreenWeb,
  trustThisDeviceWeb,
} from 'utils/experiments/features'
import { logInViewed } from 'utils/segment'
import { LOGIN_FORM } from 'utils/testing/testIds'
import withAnalyticsIdentity from 'utils/with-analytics-identity'

import { registrationUrlRedirect } from '../../utils/login-utils'
import { withAuthenticateErrorHandler } from './errors'
import AuthenticateExceptionHandler from './exceptions'
import {
  Values,
  handleFormSubmit,
  mapPropsToValues,
  validationSchema,
} from './form'
import Authenticate from './queries/authenticate.gql'
import Texts from './texts'

type CustomTextValues = {
  login: string
  signupDescription?: string
  signUpCta: string
}

export type Props = {
  children: any
  copy: string
  customTexts: CustomTextValues
  values: Values
  isSubmitting: boolean
  isValid: boolean
  openRegistration: () => void
  isModalOpen: boolean
  isValidationModalOpen: boolean
  closeModal: () => void
  handleChange: (event: FormEvent<HTMLInputElement>) => void
  closeErrorModal: () => void
  handleSubmit: () => void
  handleBlur: (event: FormEvent<HTMLInputElement>) => void
  giftcardPage: boolean
  env: any
  exception: string
  validForEmailSuspensionNotification: boolean
  analytics: GenericAnalyticsClient
  timeOnLogInView: number
  setTimeOnLogInView: (time: number) => void
  trustThisDeviceWebIsEnabled: boolean
}

const SignupContainer = styled.div`
  text-align: center;
`

const GiftcardSignupContainer = styled(SignupContainer)`
  padding: 50px;
  border-top: 1px solid ${theme.colors.ivory};
  margin: 0 -50px -40px;
`

const SignupButton = styled.button`
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  color: ${theme.colors.green};
  border: 0;
  padding: 0;
  background: #fff;

  &:hover {
    text-decoration: underline;
  }
`

const LoginButton = styled(Button)`
  width: 100%;

  &:disabled {
    background-color: ${theme.colors.stoneLight};
  }
`

const SignupTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${theme.colors.slate};
  margin-bottom: 7px;
`

const SignupCopy = styled.div`
  width: 100%;
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.5;
  color: ${theme.colors.stone};
  margin: 4px 0 10px;
`

const Form = styled('form')<Props>`
  margin-bottom: ${(props) => (props.giftcardPage ? '40px' : '1em')};
`

const withRouteSpecificText = withProps(() => {
  const { pathname } = window.location
  const customTexts = Texts[pathname] || Texts.default
  return { customTexts }
})

const SignupSection = ({ customTexts, openRegistration }) => (
  <SignupContainer>
    {customTexts.signupDescription && (
      <SignupCopy>{customTexts.signupDescription}</SignupCopy>
    )}
    <SignupButton onClick={openRegistration}>
      {customTexts.signUpCta}
    </SignupButton>
  </SignupContainer>
)

const GiftcardSignupSection = ({ customTexts, openRegistration }) => (
  <GiftcardSignupContainer>
    <SignupTitle>Don't have an account?</SignupTitle>
    <SignupCopy style={{ color: theme.colors.slate, fontWeight: 400 }}>
      {customTexts.signupDescription}
    </SignupCopy>
    <SignupButton onClick={openRegistration}>
      {customTexts.signUpCta}
    </SignupButton>
  </GiftcardSignupContainer>
)

export const enhance = compose(
  withAnalyticsClient,
  withAnalyticsIdentity,
  withEnv,
  withRouter,
  graphql(Authenticate, { name: 'login' }),
  withState('isModalOpen', 'setIsModalOpen', false),
  withState('exception', 'setException', ''),
  withState('isValidationModalOpen', 'setIsValidationModalOpen', false),
  withState('timeOnLogInView', 'setTimeOnLogInView', null),
  withState(
    'validForEmailSuspensionNotification',
    'setValidForEmailSuspensionNotification',
    false,
  ),
  withProps(() => {
    const [trustThisDeviceWebIsEnabled] = useFeature(trustThisDeviceWeb)
    return { trustThisDeviceWebIsEnabled }
  }),
  withFormik({
    validationSchema,
    mapPropsToValues,
    handleSubmit: handleFormSubmit,
  }),
  withRouteSpecificText,
  withProps(() => ({
    giftcardPage: window.location.pathname === '/redeem',
  })),
  withHandlers({
    openRegistration:
      ({ env }) =>
      () => {
        const registrationUrl = env.get('registrationUrl')
        const windowLocation = window.location
        const RegistrationUrlArgs = {
          registrationUrl,
          windowLocation,
        }

        let registrationUrlWithParams =
          registrationUrlRedirect(RegistrationUrlArgs)

        // acorns-hello uses this query string parameter to identify a new user to clear an existing session if one exists.
        registrationUrlWithParams += registrationUrlWithParams.includes('?')
          ? '&newUser=true'
          : '?newUser=true'

        window.location.href = registrationUrlWithParams
      },
    closeModal:
      ({ setIsModalOpen, setIsValidationModalOpen }) =>
      () => {
        setIsModalOpen(false)
        setIsValidationModalOpen(false)
      },
  }),
  lifecycle<Props, {}>({
    componentDidMount() {
      this.props.analytics.track('Screen Viewed', logInViewed)
      this.props.setTimeOnLogInView(Date.now())
    },
  }),
  withAuthenticateErrorHandler,
)

export const Login = ({
  closeModal,
  customTexts,
  env,
  giftcardPage,
  handleBlur,
  handleChange,
  handleSubmit,
  isModalOpen,
  isSubmitting,
  isValid,
  isValidationModalOpen,
  openRegistration,
  values,
  exception,
  validForEmailSuspensionNotification,
}: Props) => {
  const [maintenanceScreenWebIsEnabled, _, clientReady, didTimeout] =
    useFeature(maintenanceScreenWeb, {
      autoUpdate: true,
    })
  const [emailSuspensionNotificationIsEnabled] = useFeature(
    emailSuspensionNotification,
  )

  if (!clientReady && !didTimeout) {
    return null
  }

  if (maintenanceScreenWebIsEnabled) {
    return <Redirect to="/maintenance" />
  }

  return (
    <Layout copy="Log in to Acorns">
      <Form
        noValidate={true}
        data-id="login-form"
        data-testid={LOGIN_FORM}
        giftcardPage={giftcardPage}
        onSubmit={handleSubmit}
        name="login-form"
      >
        <TextInput
          id="email"
          label="Email"
          name="email"
          onBlur={handleBlur}
          onChange={handleChange}
          placeholder="name@acorns.com"
          type="email"
          value={values.email}
        />

        <PrivateInput
          helperText="Forgot password?"
          id="password"
          label="Password"
          link="/forgot-password"
          name="password"
          onBlur={handleBlur}
          onChange={handleChange}
          placeholder="●●●●●●●●"
          type="password"
          value={values.password}
        />

        <LoginButton
          disabled={isSubmitting || !isValid}
          onPress={handleSubmit}
          buttonType="submit"
          name="login-button"
        >
          {!isSubmitting ? (
            customTexts.login
          ) : (
            <Spinner height={20} width={20} color={'white'} />
          )}
        </LoginButton>
      </Form>

      {giftcardPage ? (
        <GiftcardSignupSection
          customTexts={customTexts}
          openRegistration={openRegistration}
        />
      ) : (
        <SignupSection
          customTexts={customTexts}
          openRegistration={openRegistration}
        />
      )}
      <ConfirmationModal
        isOpen={isValidationModalOpen}
        actions={[
          {
            label: 'OK',
            type: ModalActionTypes.primary,
            handler: closeModal,
          },
        ]}
        title="Invalid Email Address"
        message="Sorry, it doesn't look like the email address is valid. Please try again."
        image={AlertBadge}
      />
      <AuthenticateExceptionHandler
        exception={exception}
        closeExceptionModal={closeModal}
        openExceptionModal={isModalOpen}
        suspensionReasonValidForEmailNotification={
          validForEmailSuspensionNotification
        }
        emailNotificationEnabled={emailSuspensionNotificationIsEnabled}
      />
    </Layout>
  )
}

export default enhance(Login)
